import styled from 'styled-components'
import { Card, Heading, Text } from 'sutra/components'
import { Select } from 'sutra/components/Select/Select'
import { Toggle } from 'sutra/components/Toggle/Toggle'

import { Role } from 'types/hub-role'

/**
 * Anchor id for the view-as section. The sidebar "viewing as {role}" banner
 * links here (`/project/{id}/settings/access#view-as-role`) so clicking it
 * deep-links to this control.
 */
export const VIEW_AS_ROLE_SECTION_ID = 'view-as-role'

const ROLE_OPTIONS: Array<{ value: Role; label: string }> = [
  { value: Role.Guest, label: 'Guest' },
  { value: Role.Member, label: 'Member' },
  { value: Role.Admin, label: 'Admin' },
  { value: Role.Owner, label: 'Owner' },
]

export interface ViewAsRoleProps {
  /** The user's REAL hub role. The control renders only when this is `owner`. */
  readonly realHubRole: Role
  /** The currently-previewed role, or `null` when not previewing. */
  readonly previewedHubRole: Role | null
  /** Select a previewed role. */
  readonly onSelect: (role: Role) => void
  /** Clear the preview and return to the real role. */
  readonly onClear: () => void
}

/**
 * Owner-only, render-only "view as" control rendered as a boxed settings
 * Section. Visible iff the user's real hub role is `owner`; non-owners never
 * see it. The Switch turns preview mode on (auto-selecting `member`) or off
 * (clearing the preview); the role dropdown is enabled only while on. Selecting
 * a role changes only what the client renders — never what the backend
 * authorizes.
 */
export const ViewAsRole = ({
  realHubRole,
  previewedHubRole,
  onSelect,
  onClear,
}: ViewAsRoleProps): JSX.Element | null => {
  if (realHubRole !== Role.Owner) return null

  const isPreviewing = previewedHubRole !== null

  const onToggle = (next: boolean): void => {
    if (next) onSelect(Role.Member)
    else onClear()
  }

  return (
    <Card id={VIEW_AS_ROLE_SECTION_ID} data-testid="view-as-role">
      <HeaderRow>
        <HeaderText>
          <Heading as="h3" $level="6">
            View as role
          </Heading>
          <Text $size="sm" $tone="secondary">
            Preview the app&apos;s UI as a different role.
          </Text>
        </HeaderText>
        <Row>
          <Toggle
            aria-label="View as role"
            checked={isPreviewing}
            onChange={onToggle}
          />
          <SelectWrapper>
            <Select<Role>
              // Sutra's Select has no placeholder; while no role is previewed the
              // trigger shows a fixed prompt and the value is never displayed.
              value={previewedHubRole ?? Role.Member}
              triggerLabel={
                isPreviewing ? undefined : 'Select a role to preview'
              }
              options={ROLE_OPTIONS}
              onChange={(role) => onSelect(role)}
              disabled={!isPreviewing}
            />
          </SelectWrapper>
        </Row>
      </HeaderRow>
    </Card>
  )
}

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
`

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const SelectWrapper = styled.div`
  min-width: 200px;
`
